'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FilePropTypes } from 'DesignData/propTypes/finder';
import { isString, memoize, omit } from 'underscore';
import text from 'I18n/utils/unescapedText';
import * as AppNotificationsActions from 'DesignData/actions/AppNotifications';
import FilenameValidationErrorTypes from 'DesignData/enums/FilenameValidationErrorTypes';
import { CUSTOM_WIDGET } from 'DesignData/enums/FolderAssetTypes';
import AssetsWithFilenameExtensions from 'DesignData/constants/FolderAssetTypesWithExtensions';
import { FILENAME_MAX_LENGTH_CUSTOM_WIDGET, FILENAME_MAX_LENGTH_DEFAULT } from 'DesignData/constants/Files';
import { INVALID_CHARS, INVALID_DOT_CHAR } from 'DesignData/constants/FilenameValidation';
import { validateFilenameCharacters, validateFilenameExtension, validateFilenameLength, validateServerlessPath } from 'DesignData/lib/validation';
import { EXTENSION as SERVERLESS_EXTENSION } from 'DesignData/lib/serverless/serverless';
const isInvalidChar = memoize((key, fileType) => {
  if (AssetsWithFilenameExtensions.includes(fileType)) {
    return INVALID_CHARS.indexOf(key) !== -1;
  }

  return INVALID_CHARS.indexOf(key) !== -1 || key === INVALID_DOT_CHAR;
}, (...args) => {
  return args.join('-');
});
const mapDispatchToProps = {
  showAppNotification: AppNotificationsActions.showAppNotification
};
const propsNotUsedInRender = ['allowEmpty', 'showAppNotification'];
export default function withFilenameValidation() {
  return WrappedComponent => {
    class WithFilenameValidation extends PureComponent {
      constructor(props) {
        super(props);

        this.showValidationNotification = (notificationProps = {}) => {
          const validationMessage = this.getValidationMessage();
          if (!validationMessage) return;
          this.props.showAppNotification(Object.assign({}, notificationProps, {
            id: this.state.errorType,
            message: validationMessage,
            type: 'danger',
            titleText: 'Error'
          }));
        };

        this.setValid = value => {
          const {
            onSuccess
          } = this.props;
          this.setState({
            error: false,
            errorType: null
          });

          if (onSuccess) {
            onSuccess(value);
          }
        };

        this.setInvalid = (value, errorType) => {
          const {
            onError
          } = this.props;
          this.setState({
            error: true,
            errorType
          });

          if (onError) {
            onError(value);
          }
        };

        this.validate = value => {
          const {
            allowEmpty,
            excludeExtension,
            fileType,
            isServerlessFunctionFolder
          } = this.props;

          if (!allowEmpty && !value) {
            this.setInvalid(value, FilenameValidationErrorTypes.EMPTY_VALUE);
            return;
          }

          if (this.props.isServerlessPath) {
            if (value && !validateServerlessPath(value)) {
              this.setInvalid(value, FilenameValidationErrorTypes.INVALID_CHARACTERS);
              return;
            }

            this.setValid(value);
            return;
          }

          if (isServerlessFunctionFolder) {
            const parts = value.split('.');
            const extension = parts.pop();

            if (extension.toLowerCase() !== SERVERLESS_EXTENSION) {
              this.setInvalid(value, FilenameValidationErrorTypes.INVALID_EXTENSION);
              return;
            }

            const name = parts.join('.');

            if (!validateFilenameCharacters(name, fileType)) {
              this.setInvalid(value, FilenameValidationErrorTypes.INVALID_CHARACTERS);
              return;
            }
          }

          if (!validateFilenameLength(value, fileType)) {
            this.setInvalid(value, FilenameValidationErrorTypes.MAX_LENGTH);
            return;
          }

          if (!excludeExtension && !validateFilenameExtension(value, fileType)) {
            this.setInvalid(value, FilenameValidationErrorTypes.INVALID_EXTENSION);
            return;
          }

          if (!isServerlessFunctionFolder && !validateFilenameCharacters(value, fileType)) {
            this.setInvalid(value, FilenameValidationErrorTypes.INVALID_CHARACTERS);
            return;
          }

          this.setValid(value);
        };

        this.handleChange = evt => {
          const {
            onChange
          } = this.props;
          let value;

          if (isString(evt)) {
            value = evt;
          } else if (evt.target) {
            value = evt.target.value;
          } else {
            throw new TypeError(`Invalid onChange event object for validating. ${evt}`);
          }

          this.setState({
            value
          });

          if (onChange) {
            onChange(evt);
          }
        };

        this.handleKeyPress = evt => {
          const {
            isServerlessPath,
            fileType
          } = this.props;

          if (isServerlessPath && evt.key === '/') {
            // TODO: Externalize this logic from here
            return;
          }

          if (isInvalidChar(evt.key, fileType)) {
            evt.preventDefault();
          }
        };

        this.state = {
          error: false,
          errorType: null,
          initialValue: props.value,
          value: props.value
        };
      }

      componentDidMount() {
        const {
          validateDefaultValue,
          value
        } = this.props;

        if (validateDefaultValue && value) {
          this.validate(value);
        }
      }

      static getDerivedStateFromProps(props, state) {
        if (props.value !== state.initialValue) {
          return {
            initialValue: props.value,
            value: props.value
          };
        }

        return null;
      }

      componentDidUpdate(prevProps, prevState) {
        const {
          value
        } = this.state;

        if (value !== prevState.value) {
          this.validate(value);
        }
      }

      getValidationMessage() {
        const {
          error,
          errorType,
          fileType
        } = this.state;

        if (error) {
          let messageOptions = {};

          if (errorType === FilenameValidationErrorTypes.MAX_LENGTH) {
            messageOptions = {
              maxLength: fileType === CUSTOM_WIDGET ? FILENAME_MAX_LENGTH_CUSTOM_WIDGET : FILENAME_MAX_LENGTH_DEFAULT
            };
          }

          return text(`designComponents.filenameValidation.${errorType}`, messageOptions);
        }

        return '';
      }

      render() {
        const {
          error,
          errorType,
          initialValue,
          value
        } = this.state;
        const passedProps = omit(this.props, propsNotUsedInRender);
        return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, passedProps, {
          error: error,
          errorType: errorType,
          initialValue: initialValue,
          onChange: this.handleChange,
          onKeyPress: this.handleKeyPress,
          showValidationNotification: this.showValidationNotification,
          validationMessage: this.getValidationMessage(),
          value: value
        }));
      }

    }

    WithFilenameValidation.propTypes = {
      allowEmpty: PropTypes.bool,
      excludeExtension: PropTypes.bool.isRequired,
      fileType: FilePropTypes.fileType,
      isServerlessFunctionFolder: PropTypes.bool,
      onChange: PropTypes.func,
      onError: PropTypes.func,
      onSuccess: PropTypes.func,
      showAppNotification: PropTypes.func.isRequired,
      validateDefaultValue: PropTypes.bool.isRequired,
      value: PropTypes.string,
      isServerlessPath: PropTypes.bool
    };
    WithFilenameValidation.defaultProps = {
      allowEmpty: false,
      excludeExtension: false,
      validateDefaultValue: true
    };
    return connect(null, mapDispatchToProps)(WithFilenameValidation);
  };
}